import { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, Maximize2 } from 'lucide-react';
import { detectMediaType, getYouTubeEmbedUrl, getVimeoEmbedUrl, downloadMediaBlob } from '../../utils/mediaUtils';

const IFRAME_TYPES = new Set(['pdf', 'youtube', 'vimeo']);

/**
 * Fullscreen media lightbox — renders as a portal overlay.
 * Supports: image, video, youtube, vimeo, audio, pdf.
 */
function MediaLightbox({ url, legend, onClose }) {
    useEffect(() => {
        const handler = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handler);
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', handler);
            document.body.style.overflow = '';
        };
    }, [onClose]);

    const type = detectMediaType(url);

    const renderContent = () => {
        switch (type) {
            case 'youtube':
                return <iframe src={getYouTubeEmbedUrl(url)} className="w-[90vw] max-w-5xl aspect-video rounded-lg"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen title="YouTube" />;
            case 'vimeo':
                return <iframe src={getVimeoEmbedUrl(url)} className="w-[90vw] max-w-5xl aspect-video rounded-lg"
                    allow="autoplay; fullscreen; picture-in-picture" allowFullScreen title="Vimeo" />;
            case 'video':
                return <video src={url} controls autoPlay className="max-h-[90vh] max-w-[90vw] rounded-lg" />;
            case 'audio':
                return <audio src={url} controls autoPlay className="w-96 max-w-[90vw]" />;
            case 'pdf':
                return (
                    <div className="flex flex-col items-center gap-2 w-[90vw] max-w-5xl h-[90vh]">
                        <iframe src={url} className="w-full flex-1 rounded-lg" title="PDF" />
                        <button type="button" onClick={(e) => { e.stopPropagation(); downloadMediaBlob(url); }}
                            className="text-sm text-blue-300 hover:text-blue-100 hover:underline">
                            Télécharger le PDF
                        </button>
                    </div>
                );
            default: // image or unknown
                return <img src={url} alt="" className="max-h-[90vh] max-w-[90vw] object-contain rounded-lg" />;
        }
    };

    return createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm cursor-pointer"
            onClick={onClose}>
            {/* Close button — fixed top-right of viewport */}
            <button onClick={onClose}
                className="absolute top-4 right-4 z-10 p-2 bg-black/60 hover:bg-black/80 rounded-full text-white/90 hover:text-white transition-colors"
                title="Fermer (Echap)">
                <X size={22} />
            </button>
            <div className="relative flex flex-col items-center" onClick={e => e.stopPropagation()}>
                {renderContent()}
                {legend && <p className="text-center text-white/70 text-sm mt-3 max-w-lg">{legend}</p>}
            </div>
        </div>,
        document.body
    );
}

/**
 * Wrapper that adds expand-on-click behavior + hover icon to any media element.
 * Usage: <ExpandableMedia url={resolvedUrl} legend={legend}>{mediaElement}</ExpandableMedia>
 */
export function ExpandableMedia({ url, legend, children, className = '' }) {
    const [open, setOpen] = useState(false);
    const handleOpen = useCallback(() => setOpen(true), []);
    const handleClose = useCallback(() => setOpen(false), []);
    const isIframe = useMemo(() => IFRAME_TYPES.has(detectMediaType(url)), [url]);

    return (
        <>
            <div className={`relative group ${isIframe ? '' : 'cursor-pointer'} ${className}`}
                onClick={isIframe ? undefined : handleOpen}
                title={isIframe ? undefined : 'Cliquer pour agrandir'}>
                {children}
                <button type="button" onClick={(e) => { e.stopPropagation(); handleOpen(); }}
                    className={`absolute top-2 right-2 transition-opacity duration-200
                        bg-black/60 hover:bg-black/80 rounded-full p-1.5 cursor-pointer
                        ${isIframe ? 'opacity-80 hover:opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
                    title="Plein écran">
                    <Maximize2 size={14} className="text-white" />
                </button>
            </div>
            {open && <MediaLightbox url={url} legend={legend} onClose={handleClose} />}
        </>
    );
}

export default MediaLightbox;
